/**
 * Client Mail Service — wrapper tipizzato sui tre endpoint flusia-core per lo
 * storico email di un cliente (Gmail + Outlook, dedup cross-casella, riassunto IA).
 *
 * Backend: flusia-core `src/domains/mail/handlers/` (POST /api/mail/client-threads,
 * /api/mail/client-thread-messages, /api/mail/client-threads-summary).
 * Tipi copiati 1:1 da `flusia-core/src/domains/mail/types.ts` — non modificare le
 * shape qui senza allineare anche il core.
 *
 * Design doc: MD/features/2026-07-24-email-storico-scheda-cliente-design.md
 */

import { coreCall } from './gatewayService';
import { isOrgAiEnabledNow, isAiDisabledError } from '../hooks/useOrgAiEnabled';

export type MailProvider = 'gmail' | 'outlook';

export interface ThreadSource {
  provider: MailProvider;
  thread_id: string;
  user_id: string;
}

export interface ClientMailThread {
  thread_key: string;
  sources: ThreadSource[];
  subject: string;
  snippet: string | null;
  message_count: number;
  has_attachments: boolean;
  first_message_at: string;
  last_message_at: string;
  last_from_email: string | null;
  participants: Array<{ email: string; name: string | null }>;
  mailboxes: Array<{ user_id: string; name: string | null }>;
}

export interface ClientMailStats {
  total_threads: number;
  total_messages: number;
  last_interaction_at: string | null;
}

export interface ClientMailSummary {
  summary: string;
  generated_at: string;
  message_count: number;
}

export interface ClientMailMessage {
  provider: MailProvider;
  message_id: string;
  mailbox_user_id: string;
  from_email: string;
  from_name: string | null;
  to_emails: Array<{ email: string; name?: string }>;
  cc_emails: Array<{ email: string; name?: string }>;
  date: string;
  snippet: string | null;
  body_html: string | null;
  body_text: string | null;
  has_attachments: boolean;
}

export interface ClientMailThreadsResponse {
  threads: ClientMailThread[];
  /**
   * Cursor opaco composito `${last_message_at}|${thread_key}` generato dal core
   * per la paginazione. Non parsarlo/costruirlo lato frontend: passarlo
   * verbatim alla chiamata successiva.
   */
  next_cursor: string | null;
  stats: ClientMailStats;
  summary: ClientMailSummary | null;
  new_email_count: number;
}

/**
 * Lista paginata (cursor-based) dei thread email matchati per il cliente.
 * POST /api/mail/client-threads
 */
export async function fetchClientMailThreads(
  clientId: string,
  cursor?: string
): Promise<ClientMailThreadsResponse> {
  const { data, error } = await coreCall<ClientMailThreadsResponse>('mail/client-threads', {
    body: { client_id: clientId, cursor },
  });
  if (error || !data) throw new Error(error?.message || 'client-threads failed');
  return data;
}

/**
 * Caricamento lazy dei corpi completi dei messaggi di uno o più thread (già
 * identificati via fetchClientMailThreads) — usato quando l'utente espande un thread.
 * POST /api/mail/client-thread-messages
 */
export async function fetchClientMailThreadMessages(
  clientId: string,
  sources: ThreadSource[]
): Promise<ClientMailMessage[]> {
  const { data, error } = await coreCall<{ messages: ClientMailMessage[] }>('mail/client-thread-messages', {
    body: { client_id: clientId, sources },
  });
  if (error || !data) throw new Error(error?.message || 'client-thread-messages failed');
  return data.messages;
}

/**
 * Genera (o rigenera) il riassunto IA dello storico email del cliente.
 * POST /api/mail/client-threads-summary
 *
 * Il core risponde 409 `{error:"generation_in_progress"}` quando una generazione
 * per lo stesso cliente è già in corso (lock Redis anti-concorrenza). `coreCall`
 * appiattisce qualunque corpo errore in `error.message`, quindi qui trattiamo
 * quel messaggio specifico come esito "nessun riassunto disponibile ora" (null)
 * anziché come eccezione — il chiamante puo' ignorare/ritentare senza try/catch
 * dedicato. Qualsiasi altro errore viene invece rilanciato.
 */
export async function regenerateClientMailSummary(clientId: string): Promise<ClientMailSummary | null> {
  // Interruttore IA dell'azienda spento: nessun riassunto (le email non vanno al modello).
  // Niente toast: questa funzione parte anche da sola all'apertura delle email del cliente.
  if (!isOrgAiEnabledNow()) return null;
  const { data, error } = await coreCall<{ summary: ClientMailSummary | null }>('mail/client-threads-summary', {
    body: { client_id: clientId },
  });
  if (error) {
    if (error.message === 'generation_in_progress') return null;
    // 403 ai_disabled: il toast lo mostra gia' coreCall.
    if (isAiDisabledError(error)) return null;
    throw new Error(error.message || 'summary failed');
  }
  return data?.summary ?? null;
}
